import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button,
  Descriptions,
  Badge,
  Divider,
  Typography,
  Space,
  Tag,
  Dropdown,
  Modal,
  message,
  Col,
  Row,
  Spin,
  Alert,
  Tooltip,
} from 'antd';
import {
  EllipsisOutlined,
  EditOutlined,
  FileExcelOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { MenuProps } from 'antd/es/menu/menu';
import { Address, Estate, EstateRoleInviteResponse } from '../../types';
import Cookies from 'js-cookie';
import moment from 'moment';
import AddEstateModal from './addestate';
import EditOwnerModal from './editowner';
import InviteRoleModal from '../estateroles/inviterolemodal';
import AddParticipantModal from '../participant/addparticipant';
import ImportExportModal from '../../components/ImportExportModal';
import { deleteEstate, fetchEstateDetails } from './estateService';
import AuditTrail from '../audittrail/audittrail';
import { useGlobalContext } from '../../store/GlobalContext';
import { stateNameOf } from './usstates';

const { Title, Text } = Typography;

export const isSyntheticEmail = (email?: string | null): boolean => {
  if (!email) return true;
  return /@[a-z0-9.-]*[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(email);
};

export const formatEmail = (email?: string | null, fallback = 'Not Set'): string => {
  if (!email || isSyntheticEmail(email)) {
    return fallback;
  }
  return email;
};

const pluralize = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const fullName = (person?: { prefix?: string | null; firstName?: string | null; middleName?: string | null; lastName?: string | null; suffix?: string | null } | null) =>
  [person?.prefix, person?.firstName, person?.middleName, person?.lastName, person?.suffix]
    .filter(Boolean)
    .join(' ');

const formatAddress = (address?: Partial<Address> | null) => {
  if (!address) return '';

  const street = [address.addressLine1, address.addressLine2].filter(Boolean).join(', ');
  const region = [address.city, address.state, address.zipCode].filter(Boolean).join(' ');
  const county = address.county ? `${address.county} County` : '';

  return [street, region, county].filter(Boolean).join(', ');
};

/** The estate's own address, held on the estate rather than on any person. */
const estateAddress = (estate?: Estate | null): Partial<Address> => ({
  addressLine1: estate?.addressLine1 || '',
  addressLine2: estate?.addressLine2 || '',
  city: estate?.city || '',
  state: estate?.state || '',
  zipCode: estate?.zipCode || '',
  county: estate?.county || '',
});

// Styled Components
const EstateContainer = styled.div`
  max-width: 800px;
  margin: auto;
  padding: 1rem;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  /* The actions outgrew one row once the estate, owner and decedent each had
     their own. Wrapping puts them on a second line instead of overflowing the
     card and clipping the last of them. */
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

/*
 * Flex items shrink below their content by default, which squeezed the estate
 * title into a column one character wide once the actions needed the room.
 */
const HeaderTitle = styled.div`
  flex: 0 0 auto;
  min-width: 220px;
`;

/*
 * A headcount only. The rosters and their actions live on the Participants
 * page; this is the pointer to them.
 */
const PeopleSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  background-color: #f4f6fb;
  border: 1px solid #e0e0e0;
`;

const EstateDetails: React.FC = () => {
  const { estateId } = useParams<{ estateId: string }>();
  const navigate = useNavigate();
  const { selectEstate } = useGlobalContext();
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isEditOwnerModalVisible, setIsEditOwnerModalVisible] = useState(false);
  const [isAddParticipantModalVisible, setIsAddParticipantModalVisible] = useState(false);
  const [isAddExecutorModalVisible, setIsAddExecutorModalVisible] = useState(false);
  const [isAddLawyerModalVisible, setIsAddLawyerModalVisible] = useState(false);
  const [isImportExportModalVisible, setIsImportExportModalVisible] = useState(false);

  const [estateDetails, setEstateDetails] = useState<Estate | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const participantCount = estateDetails?.participants?.length || 0;
  const executorCount = estateDetails?.executor?.length || 0;
  const lawyerCount = estateDetails?.lawyers?.length || 0;
  const inviteCount = estateDetails?.invites?.length || 0;
  const guestCount = estateDetails?.guests?.length || 0;

  /*
   * The Participants page reads the active estate, not a route param, so make
   * the estate being viewed the active one before handing over. Otherwise the
   * link would show whichever estate happened to be selected last.
   */
  const handleViewParticipants = () => {
    if (estateDetails) {
      selectEstate(estateDetails);
    }
    navigate('/participants');
  };

  /*
   * Estate-level actions. The three things an estate holds are edited apart:
   * the estate itself has its own button, and the two people, its owner and its
   * decedent, have an entry each here. They are separate records, and a
   * correction to one should never rewrite the other.
   */
  const estateActionsMenuItems: MenuProps['items'] = [
    {
      label: 'Edit Owner',
      key: 'editOwner',
    },
    { type: 'divider' },
    {
      label: 'Delete Estate',
      key: 'delete',
      danger: true,
    },
  ];

  // Handle click on the estate actions menu
  const handleEstateActionClick: MenuProps['onClick'] = (e) => {
    if (e.key === 'editOwner') {
      setIsEditOwnerModalVisible(true);
    }
    if (e.key === 'delete') {
      handleDeleteEstate();
    }
  };

  const estateActionsMenuProps = {
    items: estateActionsMenuItems,
    onClick: handleEstateActionClick,
  };

  // Function to delete the estate
  const handleDeleteEstate = () => {
    Modal.confirm({
      title: 'Confirm Estate Deletion',
      content: 'Are you sure you want to delete this estate?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        try {
          const token = Cookies.get('token') || Cookies.get('accessToken');
          if (!token) {
            message.error('Unauthorized. Please log in.');
            return;
          }

          await deleteEstate(estateId!, token);
          message.success('Estate deleted successfully');
          navigate('/estates'); // Navigate back to the estates list page (adjust as needed)
        } catch (error: any) {
          console.error(error);
          message.error(error.message || 'Failed to delete estate');
        }
      },
    });
  };

  // Fetch Estate Details (for viewing/editing an existing estate)
  const loadEstateDetails = useCallback(async () => {
    // Only fetch if estateId is provided (i.e., viewing/editing an existing estate)
    if (!estateId) {
      setIsLoading(false); // No estate to load
      return;
    }

    try {
      const token = Cookies.get('token') || Cookies.get('accessToken'); // Adjust the cookie name as needed
      if (!token) {
        message.error('Unauthorized. Please log in.');
        navigate('/login'); // Redirect to login or appropriate page
        return;
      }

      const estateData: Estate = await fetchEstateDetails(estateId, token);
      setEstateDetails({ ...estateData });
    } catch (err: any) {
      console.error(err);
      if (err.message === 'Unauthorized') {
        message.error('Unauthorized. Please log in.');
        navigate('/login');
      } else {
        setError(err.message || 'Failed to load estate details.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [estateId, navigate]);

  useEffect(() => {
    loadEstateDetails();
  }, [loadEstateDetails]);

  // Handle Success of Estate Edit or Create
  const handleEstateSubmit = (estate: Estate) => {
    if (estateId) {
      // Editing existing estate
      setEstateDetails(estate);
      message.success('Estate details updated successfully');
    } else {
      // Creating new estate
      setEstateDetails(estate);
      message.success('Estate created successfully');
      // Optionally, navigate to the new estate's details page
      navigate(`/estates/${estate.uuid}`);
    }
  };

  // The owner and decedent forms return the whole estate, and announce their
  // own success, so this only needs to take the fresh copy.
  const handleEstatePersonSaved = (estate: Estate) => {
    setEstateDetails(estate);
  };

  // Handle Success of Adding a Participant. The roster lives on the
  // Participants page, so refresh the estate to keep the headcount honest.
  // The modal announces the save itself.
  const handleParticipantSuccess = () => {
    setIsAddParticipantModalVisible(false);
    loadEstateDetails();
  };

  // Handle Success of an Executor or Lawyer Invitation. The invite creates a
  // pending record rather than a roster entry, so the counts above only move
  // once it is accepted.
  const handleEstateRoleInviteSuccess = (response: EstateRoleInviteResponse) => {
    message.success(response.message);
  };

  const estateAddressText = formatAddress(estateAddress(estateDetails));

  if (isLoading) {
    return (
      <EstateContainer>
        <Spin tip="Loading estate details..." />
      </EstateContainer>
    );
  }

  if (error) {
    return (
      <EstateContainer>
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          action={
            <Button type="primary" onClick={() => window.location.reload()}>
              Retry
            </Button>
          }
        />
      </EstateContainer>
    );
  }

  return (
    <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
      {/* Estate Details and Actions */}
      <Col xs={24} lg={16}>
        <EstateContainer>
          {/* Custom Header */}
          <HeaderContainer>
            <HeaderTitle>
              <Space direction="vertical" size={2}>
                <Title level={4} style={{ margin: 0 }}>
                  {estateDetails?.estateName ? `${estateDetails.estateName} Estate` : 'Estate Details'}
                </Title>
                <Space size="middle" align="center">
                  <Badge status="processing" text="In progress" />
                  {estateId && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      ID: {estateId.length > 8 ? `${estateId.slice(0, 8)}...` : estateId}
                    </Text>
                  )}
                </Space>
              </Space>
            </HeaderTitle>
            <Space wrap>
              {estateId && (
                <Tooltip title="Bulk Import/Export Assets, Liabilities, Participants, and Cashflows">
                  <Button
                    icon={<FileExcelOutlined />}
                    onClick={() => setIsImportExportModalVisible(true)}
                  >
                    Import/Export
                  </Button>
                </Tooltip>
              )}
              {estateId ? (
                <>
                  <Button type="primary" onClick={() => setIsAddParticipantModalVisible(true)}>
                    Add Participant
                  </Button>
                  <Button onClick={() => setIsAddExecutorModalVisible(true)}>
                    Add Executor
                  </Button>
                  <Button onClick={() => setIsAddLawyerModalVisible(true)}>
                    Add Lawyer
                  </Button>
                </>
              ) : (
                <Button type="primary" onClick={() => navigate('/create-estate')}>
                  Create Estate
                </Button>
              )}
              <Dropdown menu={estateActionsMenuProps}>
                <Button icon={<EllipsisOutlined />} />
              </Dropdown>
            </Space>
          </HeaderContainer>

          {estateId && (
            <>
              <Descriptions
                title={<Text strong style={{ fontSize: '16px' }}>Estate Details</Text>}
                extra={
                  <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => setIsEditModalVisible(true)}
                  >
                    Edit Estate
                  </Button>
                }
                size="small"
                column={2}
                bordered={false}
              >
                <Descriptions.Item label="Estate Name">
                  {estateDetails?.estateName || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Jurisdiction State">
                  {stateNameOf(estateDetails?.state) || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="County">
                  {estateDetails?.county || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="EIN">
                  {estateDetails?.ein || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Start Date">
                  {estateDetails?.createdAt
                    ? moment(estateDetails.createdAt).format('LL')
                    : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Estate Address" span={2}>
                  {estateAddressText || estateDetails?.address || 'N/A'}
                </Descriptions.Item>
              </Descriptions>

              <Divider style={{ margin: '16px 0' }} />

              <Descriptions
                title={<Text strong style={{ fontSize: '16px' }}>Owner Details</Text>}
                extra={
                  <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => setIsEditOwnerModalVisible(true)}
                  >
                    Edit Owner
                  </Button>
                }
                size="small"
                column={2}
                bordered={false}
              >
                <Descriptions.Item label="Owner Name">
                  {fullName(estateDetails?.estateOwner) || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                    <span style={{ wordBreak: 'break-all' }}>
                      {formatEmail(estateDetails?.estateOwner?.email)}
                    </span>
                    {estateDetails?.estateOwner?.pendingEmail && (
                      <Tag color="gold" style={{ marginTop: 2 }}>
                        Pending: {estateDetails.estateOwner.pendingEmail}
                      </Tag>
                    )}
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="Phone">
                  {estateDetails?.estateOwner?.phone || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Date of Birth">
                  {estateDetails?.estateOwner?.dob
                    ? moment(estateDetails.estateOwner.dob).format('LL')
                    : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Marital Status">
                  {estateDetails?.estateOwner?.maritalStatus
                    ? estateDetails.estateOwner.maritalStatus.charAt(0).toUpperCase() + estateDetails.estateOwner.maritalStatus.slice(1)
                    : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Citizenship">
                  {(estateDetails?.estateOwner as any)?.citizenship || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="SSN">
                  {(estateDetails?.estateOwner as any)?.ssn || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Sex">
                  {estateDetails?.estateOwner?.sex
                    ? estateDetails.estateOwner.sex.charAt(0).toUpperCase() + estateDetails.estateOwner.sex.slice(1)
                    : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  {estateDetails?.estateOwner?.deceased ? (
                    <Tag color="red">Deceased</Tag>
                  ) : (
                    <Tag color="green">Living</Tag>
                  )}
                </Descriptions.Item>
                {estateDetails?.estateOwner?.deceased && (
                  <>
                    <Descriptions.Item label="Date of Death">
                      {estateDetails.estateOwner.dateOfDeath
                        ? moment(estateDetails.estateOwner.dateOfDeath).format('LL')
                        : 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Place of Death Name">
                      {(estateDetails.estateOwner as any)?.placeOfDeathName || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Place of Death Address" span={2}>
                      {(estateDetails.estateOwner as any)?.placeOfDeathAddress || 'N/A'}
                    </Descriptions.Item>
                  </>
                )}
                <Descriptions.Item label="Domicile Address" span={2}>
                  {formatAddress(((estateDetails?.estateOwner as any)?.addresses || []).find((a: any) => a.addressType === 'domicile') || ((estateDetails?.estateOwner as any)?.addresses || [])[0]) || 'N/A'}
                </Descriptions.Item>
                {((estateDetails?.estateOwner as any)?.addresses || []).find((a: any) => a.addressType === 'mailing') && (
                  <Descriptions.Item label="Mailing Address" span={2}>
                    {formatAddress(((estateDetails?.estateOwner as any)?.addresses || []).find((a: any) => a.addressType === 'mailing')) || 'N/A'}
                  </Descriptions.Item>
                )}
                {((estateDetails?.estateOwner as any)?.addresses || []).find((a: any) => a.addressType === 'present' || a.addressType === 'billing') && (
                  <Descriptions.Item label="Present Address" span={2}>
                    {formatAddress(((estateDetails?.estateOwner as any)?.addresses || []).find((a: any) => a.addressType === 'present' || a.addressType === 'billing')) || 'N/A'}
                  </Descriptions.Item>
                )}
              </Descriptions>

              <Divider style={{ margin: '16px 0' }} />

              {/*
                The rosters themselves (participants, executors, lawyers,
                invites and guests) and their edit and delete actions now live
                on the Participants page. This page keeps the buttons that add
                to them.
              */}
              <PeopleSummary>
                <Space direction="vertical" size={4}>
                  <Text strong style={{ fontSize: '16px' }}>People</Text>
                  <Space size="middle" wrap>
                    <Text type="secondary">{pluralize(participantCount, 'participant')}</Text>
                    <Text type="secondary">{pluralize(executorCount, 'executor')}</Text>
                    <Text type="secondary">{pluralize(lawyerCount, 'lawyer')}</Text>
                    <Text type="secondary">{pluralize(inviteCount, 'invite')}</Text>
                    <Text type="secondary">{pluralize(guestCount, 'guest')}</Text>
                  </Space>
                </Space>
                <Button type="link" icon={<TeamOutlined />} onClick={handleViewParticipants}>
                  Manage Participants
                </Button>
              </PeopleSummary>
            </>
          )}

          {/* Edit Estate Modal */}
          {estateId && (
            <AddEstateModal
              open={isEditModalVisible}
              onCancel={() => setIsEditModalVisible(false)}
              onSubmit={handleEstateSubmit}
              initialValues={
                estateDetails
                  ? {
                    uuid: estateDetails.uuid,
                    estateName: estateDetails.estateName,
                    description: estateDetails.description,
                    ein: estateDetails.ein || '',
                    state: estateDetails.state || undefined,
                    county: estateDetails.county || '',
                    addressLine1: estateDetails.addressLine1 || '',
                    addressLine2: estateDetails.addressLine2 || '',
                    city: estateDetails.city || '',
                    zipCode: estateDetails.zipCode || '',
                  }
                  : undefined
              }
              isEdit={true}
            />
          )}

          {/* Edit Owner Modal */}
          {estateId && (
            <EditOwnerModal
              open={isEditOwnerModalVisible}
              estateId={estateId}
              owner={estateDetails?.estateOwner}
              onCancel={() => setIsEditOwnerModalVisible(false)}
              onSubmit={handleEstatePersonSaved}
            />
          )}

          {/* Import/Export Modal */}
          {estateId && (
            <ImportExportModal
              open={isImportExportModalVisible}
              onClose={() => setIsImportExportModalVisible(false)}
              estateId={estateId!}
            />
          )}

          {/* Add Participant Modal. Editing happens on the Participants page. */}
          <AddParticipantModal
            visible={isAddParticipantModalVisible}
            onCancel={() => setIsAddParticipantModalVisible(false)}
            onSuccess={handleParticipantSuccess}
            estateId={estateId!}
            estate={estateDetails}
          />

          {/* Add Executor Modal */}
          {estateId && (
            <InviteRoleModal
              open={isAddExecutorModalVisible}
              onCancel={() => setIsAddExecutorModalVisible(false)}
              onSuccess={handleEstateRoleInviteSuccess}
              estateId={estateId!}
              role="executor"
            />
          )}

          {/* Add Lawyer Modal */}
          {estateId && (
            <InviteRoleModal
              open={isAddLawyerModalVisible}
              onCancel={() => setIsAddLawyerModalVisible(false)}
              onSuccess={handleEstateRoleInviteSuccess}
              estateId={estateId!}
              role="lawyer"
            />
          )}
        </EstateContainer>
      </Col>
      <Col xs={24} lg={8}>
        <AuditTrail />
      </Col>
    </Row>
  );
};

export default EstateDetails;
